<template>
  <div class="safety-info-survey-table">
    <!-- 섹션 헤더 -->
    <div class="flex items-center mb-4">
      <h5 class="flex items-center"><i class="dot"></i>{{ $t("안전보건 정보조사 결과") }}</h5>
      <div v-if="!readonly && !isPrint" class="ml-auto flex">
        <vs-button color="secondary" @click="handleAddRow">{{ $t("항목추가") }}</vs-button>
        <vs-button class="ml-2" color="secondary" type="border" :disabled="checkData.length === 0" @click="handleRemoveRow">{{ $t("선택삭제") }}</vs-button>
      </div>
    </div>

    <!-- 데스크탑 / 인쇄: 테이블 -->
    <div :class="{ 'hidden xl:block': !isPrint }" class="table-wrap">
      <table>
        <colgroup>
          <col v-if="!readonly && !isPrint" style="width: 86px" />
          <col style="min-width: 140px" />
          <col style="min-width: 220px" />
          <col style="width: 110px" />
          <col style="min-width: 180px" />
          <col style="width: 130px" />
          <col style="width: 130px" />
          <col style="min-width: 200px" />
        </colgroup>
        <thead>
          <tr>
            <th v-if="!readonly && !isPrint" rowspan="2">
              <vs-checkbox :value="allChecked" class="justify-center mx-0" @click.native="onClickAllCheck" />
            </th>
            <th rowspan="2">{{ $t("공정(작업)순서") }}</th>
            <th colspan="2">{{ $t("기계·기구 및 설비") }}</th>
            <th colspan="3">{{ $t("유해화학물질") }}</th>
            <th rowspan="2">{{ $t("그 밖의 유해·위험정보") }}</th>
          </tr>
          <tr>
            <th>{{ $t("기계·기구 및 설비명") }}</th>
            <th>{{ $t("수량") }}</th>
            <th>{{ $t("화학물질명") }}</th>
            <th>{{ $t("취급량/일") }}</th>
            <th>{{ $t("취급시간") }}</th>
          </tr>
        </thead>

        <!-- 편집 -->
        <draggable v-if="!readonly && !isPrint" v-model="list" tag="tbody" handle=".drag-handle" :animation="150">
          <tr v-for="group in list" :key="group.id">
            <td class="check-cell">
              <div class="check-cell-inner">
                <vs-checkbox v-model="checkData" :vs-value="group.id" class="mx-0" />
                <feather-icon icon="MenuIcon" class="drag-handle" />
              </div>
            </td>
            <td>
              <ml-input v-model="group.공정명" :placeholder="$t('공정명')" class="w-full" />
            </td>
            <td class="stacked-cell">
              <div v-for="(item, iIdx) in group.items" :key="`m_${item.id}`" class="sub-row">
                <button
                  v-if="iIdx === 0"
                  type="button"
                  class="sub-action-btn add"
                  :title="$t('항목추가')"
                  @click="handleAddSubItem(group)">
                  +
                </button>
                <button
                  v-else
                  type="button"
                  class="sub-action-btn remove"
                  :title="$t('항목삭제')"
                  @click="handleRemoveSubItem(group, item)">
                  −
                </button>
                <ml-input v-model="item.기계기구설비명" :placeholder="$t('기계·기구 및 설비명')" class="w-full" />
              </div>
            </td>
            <td class="stacked-cell">
              <div v-for="item in group.items" :key="`q_${item.id}`" class="sub-row">
                <ml-input v-model="item.수량" :placeholder="$t('수량')" class="w-full" type="number" />
              </div>
            </td>
            <td class="stacked-cell">
              <div v-for="item in group.items" :key="`c_${item.id}`" class="sub-row">
                <ml-input v-model="item.화학물질명" :placeholder="$t('화학물질명')" class="w-full" />
              </div>
            </td>
            <td class="stacked-cell">
              <div v-for="item in group.items" :key="`a_${item.id}`" class="sub-row">
                <ml-input v-model="item.취급량일" :placeholder="$t('취급량/일')" class="w-full" />
              </div>
            </td>
            <td class="stacked-cell">
              <div v-for="item in group.items" :key="`t_${item.id}`" class="sub-row">
                <ml-input v-model="item.취급시간" :placeholder="$t('취급시간')" class="w-full" />
              </div>
            </td>
            <td>
              <ml-input v-model="group.그밖의유해위험정보" :placeholder="$t('조사결과 참고')" class="w-full" />
            </td>
          </tr>
        </draggable>

        <!-- 읽기 / 인쇄 -->
        <tbody v-else>
          <tr v-for="group in list" :key="`ro_${group.id}`">
            <td>
              <div class="print-input">{{ group.공정명 }}</div>
            </td>
            <td class="stacked-cell">
              <div v-for="item in group.items" :key="`mr_${item.id}`" class="sub-row">
                <div class="print-input w-full">{{ item.기계기구설비명 }}</div>
              </div>
            </td>
            <td class="stacked-cell">
              <div v-for="item in group.items" :key="`qr_${item.id}`" class="sub-row">
                <div class="print-input w-full">{{ item.수량 }}</div>
              </div>
            </td>
            <td class="stacked-cell">
              <div v-for="item in group.items" :key="`cr_${item.id}`" class="sub-row">
                <div class="print-input w-full">{{ item.화학물질명 }}</div>
              </div>
            </td>
            <td class="stacked-cell">
              <div v-for="item in group.items" :key="`ar_${item.id}`" class="sub-row">
                <div class="print-input w-full">{{ item.취급량일 }}</div>
              </div>
            </td>
            <td class="stacked-cell">
              <div v-for="item in group.items" :key="`tr_${item.id}`" class="sub-row">
                <div class="print-input w-full">{{ item.취급시간 }}</div>
              </div>
            </td>
            <td>
              <div class="print-input">{{ group.그밖의유해위험정보 }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 모바일: 카드 -->
    <div v-if="!isPrint" class="xl:hidden mobile-view">
      <div v-if="!readonly" class="all-select-row">
        <span class="all-select-title">{{ $t("안전보건 정보조사 결과") }}</span>
        <label class="all-select-label">
          <span>{{ $t("전체선택") }}</span>
          <vs-checkbox :value="allChecked" class="mx-0" @click.native="onClickAllCheck" />
        </label>
      </div>

      <draggable v-if="!readonly" v-model="list" handle=".drag-handle" :animation="150">
        <div v-for="(group, gIdx) in list" :key="`m_${group.id}`" class="survey-card">
          <div class="card-header">
            <span class="index">({{ gIdx + 1 }})</span>
            <div class="card-header-actions">
              <vs-checkbox v-model="checkData" :vs-value="group.id" class="mx-0" />
              <feather-icon icon="MenuIcon" class="drag-handle" />
            </div>
          </div>
          <div class="card-body">
            <div class="field">
              <label class="field-label">{{ $t("공정(작업)순서") }}</label>
              <div class="field-input">
                <ml-input v-model="group.공정명" :placeholder="$t('공정명')" class="w-full" />
              </div>
            </div>

            <div class="sub-items">
              <div v-for="(item, iIdx) in group.items" :key="`m_${group.id}_${item.id}`" class="sub-item">
                <div class="sub-item-header">
                  <span class="sub-index">{{ iIdx + 1 }}</span>
                  <div class="sub-item-actions">
                    <button v-if="iIdx === 0" type="button" class="sub-action-btn add" @click="handleAddSubItem(group)">+</button>
                    <button v-else type="button" class="sub-action-btn remove" @click="handleRemoveSubItem(group, item)">−</button>
                  </div>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("기계·기구 및 설비명") }}</label>
                  <div class="field-input">
                    <ml-input v-model="item.기계기구설비명" :placeholder="$t('기계·기구 및 설비명')" class="w-full" />
                  </div>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("수량") }}</label>
                  <div class="field-input">
                    <ml-input v-model="item.수량" :placeholder="$t('수량')" class="w-full" type="number" />
                  </div>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("화학물질명") }}</label>
                  <div class="field-input">
                    <ml-input v-model="item.화학물질명" :placeholder="$t('화학물질명')" class="w-full" />
                  </div>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("취급량/일") }}</label>
                  <div class="field-input">
                    <ml-input v-model="item.취급량일" :placeholder="$t('취급량/일')" class="w-full" />
                  </div>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("취급시간") }}</label>
                  <div class="field-input">
                    <ml-input v-model="item.취급시간" :placeholder="$t('취급시간')" class="w-full" />
                  </div>
                </div>
              </div>
            </div>

            <div class="field">
              <label class="field-label">{{ $t("그 밖의 유해·위험정보") }}</label>
              <div class="field-input">
                <ml-input v-model="group.그밖의유해위험정보" :placeholder="$t('조사결과 참고')" class="w-full" />
              </div>
            </div>
          </div>
        </div>
      </draggable>

      <div v-else>
        <div v-for="(group, gIdx) in list" :key="`mr_${group.id}`" class="survey-card readonly">
          <div class="card-header">
            <span class="index">({{ gIdx + 1 }})</span>
          </div>
          <div class="card-body">
            <div class="field">
              <label class="field-label">{{ $t("공정(작업)순서") }}</label>
              <div class="field-input print-input">{{ group.공정명 }}</div>
            </div>

            <div class="sub-items">
              <div v-for="(item, iIdx) in group.items" :key="`mr_${group.id}_${item.id}`" class="sub-item">
                <div class="sub-item-header">
                  <span class="sub-index">{{ iIdx + 1 }}</span>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("기계·기구 및 설비명") }}</label>
                  <div class="field-input print-input">{{ item.기계기구설비명 }}</div>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("수량") }}</label>
                  <div class="field-input print-input">{{ item.수량 }}</div>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("화학물질명") }}</label>
                  <div class="field-input print-input">{{ item.화학물질명 }}</div>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("취급량/일") }}</label>
                  <div class="field-input print-input">{{ item.취급량일 }}</div>
                </div>
                <div class="field">
                  <label class="field-label">{{ $t("취급시간") }}</label>
                  <div class="field-input print-input">{{ item.취급시간 }}</div>
                </div>
              </div>
            </div>

            <div class="field">
              <label class="field-label">{{ $t("그 밖의 유해·위험정보") }}</label>
              <div class="field-input print-input">{{ group.그밖의유해위험정보 }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: "SafetyInfoSurveyTable",
  components: { draggable },
  props: {
    value: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    isPrint: { type: Boolean, default: false },
  },
  data() {
    return {
      list: this.value,
      checkData: [],
    };
  },
  computed: {
    allChecked() {
      return this.list.length > 0 && this.checkData.length === this.list.length;
    },
  },
  watch: {
    value: {
      handler(value) {
        this.list = value;
      },
    },
    list: {
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  mounted() {
    if (!this.readonly && !this.isPrint && this.list.length < 1) {
      this.handleAddRow();
    }
  },
  methods: {
    generateId() {
      return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    },
    createSubItem() {
      return {
        id: this.generateId(),
        기계기구설비명: "",
        수량: "",
        화학물질명: "",
        취급량일: "",
        취급시간: "",
      };
    },
    handleAddRow() {
      this.list.push({
        id: this.generateId(),
        공정명: "",
        그밖의유해위험정보: "",
        items: [this.createSubItem()],
      });
    },
    handleRemoveRow() {
      this.list = this.list.filter((group) => !this.checkData.includes(group.id));
      this.checkData = [];
    },
    onClickAllCheck() {
      if (this.checkData.length === this.list.length) this.checkData = [];
      else this.checkData = this.list.map((group) => group.id);
    },
    handleAddSubItem(group) {
      group.items.push(this.createSubItem());
    },
    handleRemoveSubItem(group, item) {
      if (group.items.length <= 1) return;
      group.items = group.items.filter((it) => it.id !== item.id);
    },
  },
};
</script>

<style lang="scss" scoped>
$border-color: #cdcdcd;

.table-wrap {
  width: 100%;
  overflow: visible;

  > table {
    table-layout: fixed;
    border-collapse: collapse;
    width: 100%;

    th,
    td {
      border: 1px solid $border-color;
      padding: 8px;
      vertical-align: middle;

      &::v-deep .con-vs-checkbox {
        justify-content: center !important;
        margin: 0;
        span {
          margin: 0;
        }
      }
    }

    th {
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
      color: #3c3c3c;
      text-align: center;
      background: #f9f9f9;
      padding: 12px 8px;
    }
  }
}

.check-cell {
  background: #f9f9f9;
  vertical-align: middle;

  .check-cell-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  &::v-deep .con-vs-checkbox {
    display: inline-flex !important;
    margin: 0 !important;
  }
}

.drag-handle {
  width: 16px;
  height: 16px;
  cursor: grab;
  color: #8c8c8c;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
}

/* sub-row가 세로로 쌓이는 셀: 셀 자체의 패딩 제거하고 각 sub-row에서 패딩 처리 */
.stacked-cell {
  padding: 0 !important;
  vertical-align: top;

  .sub-row {
    padding: 8px;
    min-height: 54px;
    display: flex;
    align-items: center;
    gap: 8px;

    &:not(:last-child) {
      border-bottom: 1px solid $border-color;
    }
  }
}

.sub-action-btn {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid $border-color;
  background: #ffffff;
  color: #3c3c3c;
  font-size: 16px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: #f4f4f4;
  }

  &.add {
    color: #3c3c3c;
  }

  &.remove {
    color: #6e6e6e;
  }
}

/* drag 도중에 시각화 */
.sortable-ghost {
  opacity: 0.5;
  background: #eef2ff;
}

/* 모바일 카드 뷰 */
.mobile-view {
  .all-select-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 12px;
    border-top: 1px solid $border-color;
    border-bottom: 1px solid $border-color;
    background: #f9f9f9;

    .all-select-title {
      font-weight: 500;
      font-size: 14px;
      color: #3c3c3c;
    }

    .all-select-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #3c3c3c;
      cursor: pointer;
      margin-bottom: 0;
    }
  }

  .survey-card {
    border: 1px solid $border-color;
    border-top: none;
    background: #fff;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 16px;
      background: #f9f9f9;
      border-bottom: 1px solid $border-color;

      .index {
        font-weight: 500;
        font-size: 14px;
        color: #3c3c3c;
      }

      .card-header-actions {
        display: flex;
        align-items: center;
        gap: 10px;
      }
    }

    .card-body {
      padding: 16px;

      .field {
        display: flex;
        align-items: flex-start;
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        .field-label {
          width: 130px;
          flex-shrink: 0;
          font-size: 14px;
          color: #3c3c3c;
          margin-bottom: 0;
          padding-top: 8px;
        }

        .field-input {
          flex: 1;
          min-width: 0;
        }
      }

      .sub-items {
        margin: 8px 0 16px;
        border-top: 1px dashed $border-color;
        padding-top: 12px;

        .sub-item {
          border: 1px solid $border-color;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 12px;
          background: #fafafa;

          &:last-child {
            margin-bottom: 0;
          }

          .sub-item-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 10px;

            .sub-index {
              font-size: 13px;
              color: #6e6e6e;
              font-weight: 500;
            }
          }
        }
      }
    }

    &.readonly .card-header {
      justify-content: flex-start;
    }
  }
}
</style>
